import { NgClass } from '@angular/common'
import { Component, inject, NgZone, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core'
import { ActivationEnd } from '@angular/router'
import { Subject, Subscription } from 'rxjs'

import { ConfirmationService, PrimeTemplate } from 'primeng/api'
import { ButtonDirective } from 'primeng/button'
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog'
import { FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload'

import { map } from 'lodash-es'

import { ArchiveDocumentForm } from '@app/interfaces/archive-documents'
import { User } from '@app/interfaces/user'
import { ArchiveDocumentsApi } from '@app/apis/archive-documents.api'
import { Signal, SignalService } from '@app/services/signal.service'
import { UpdateService } from '@app/services/update.service'
import { ConfigureFiltersComponent } from '@app/components/configure-filters/configure-filters'
import { GenericColumnComponent } from '@app/components/generic/generic-column'
import { GenericListComponent } from '@app/components/generic/generic-list'
import { MetadataComponent } from '@app/components/metadata/metadata.component'
import type { ArchiveDocumentListItem, PaginatedArchiveDocumentListItemList } from '@app/generated/api/model'

import { FileUploadModalComponent } from './file-upload-modal.component'

@Component({
  selector: 'app-drafts',
  templateUrl: 'drafts.component.html',
  styleUrl: 'drafts.component.less',
  encapsulation: ViewEncapsulation.None,
  imports: [
    FileUpload,
    PrimeTemplate,
    ButtonDirective,
    ConfigureFiltersComponent,
    GenericListComponent,
    GenericColumnComponent,
    NgClass,
  ],
})
export class DraftsComponent extends GenericListComponent<ArchiveDocumentListItem> implements OnInit, OnDestroy {
  public readonly zone = inject(NgZone)
  public readonly confirmationService = inject(ConfirmationService)
  private readonly archiveDocumentsApi = inject(ArchiveDocumentsApi)
  private readonly signalService = inject(SignalService)
  public readonly updateService = inject(UpdateService) // updateService required to be here in order for the service to be created and used
  public readonly dialogService = inject(DialogService)

  public drafts: ArchiveDocumentListItem[] = []

  public userSubscription!: Subscription
  public routerEventsSubscription?: Subscription
  public signalServiceSubscription?: Subscription

  // used for drafts generic list
  public tableReloadSubject: Subject<void> = new Subject<void>()
  public override totalRecords: number = 0
  public selectedDocuments: ArchiveDocumentListItem[] = []

  // uuid of currently-active document
  public activeUuid: string = ''
  public user?: User

  // whether to disable the add drafts button, because the user already has too many
  public disableAddDrafts: boolean = false

  @ViewChild(GenericListComponent) private genericListComponent!: GenericListComponent<ArchiveDocumentListItem>
  @ViewChild('theFileUpload') public fileUploadControl!: FileUpload

  override ngOnInit (): void {
    // generic list boilerplate
    this.api = this.archiveDocumentsApi
    this.loading = true

    // used for drop-down filters
    if (this.locale === 'fr') {
      this.filterLabelField = 'label_fr'
    }

    /**
     * Subscribe to the user login event
     */
    this.userSubscription = this.userService.getUser().subscribe(
      (user) => {
        this.user = user
        // set the mandatory filters for the drafts list
        this.forceFilters = { state: 'new', created_by__username: user.username }
      },
    )

    // we can't use the ActivatedRoute to determine the current route coz we're outside of the route-outlet,
    // so we must determine it like this:
    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      if (event instanceof ActivationEnd) {
        if (event.snapshot.paramMap.get('code')) {
          this.activeUuid = event.snapshot.paramMap.get('code') ?? ''
        }
      }
    })

    // subscribe to the signal that will be used to refresh the draft files list, for example following a bulk-update operation
    this.signalServiceSubscription = this.signalService.getSignal().subscribe((signal: string) => {
      if (signal === Signal.SIGNAL_REFRESH_DRAFTS) {
        this.zone.run(() => {
          this.tableReloadSubject.next()
        })
      }
    })

    super.ngOnInit()
  }

  override ngOnDestroy (): void {
    this.userSubscription?.unsubscribe()
    this.routerEventsSubscription?.unsubscribe()
    this.signalServiceSubscription?.unsubscribe()
    super.ngOnDestroy()
  }

  /**
   * Set the current selection and show or hide the viewer pane mask as required
   */
  selectDocuments (selectedDocuments: ArchiveDocumentListItem[]) {
    this.selectedDocuments = selectedDocuments

    // set the view pane mask visibility
    if (this.selectedDocuments.length > 0) {
      this.signalService.sendSignal(Signal.SIGNAL_SHOW_MASK_VIEWER_PANE)
    } else {
      this.signalService.sendSignal(Signal.SIGNAL_HIDE_MASK_VIEWER_PANE)
    }
  }

  /**
   * Present the multiple update metadata dialog
   */
  showUpdateMetadataDialog (): DynamicDialogRef<MetadataComponent> | null {
    const selectedDocuments = this.selectedDocuments

    const ref = this.dialogService.open(MetadataComponent, {
      header: $localize`:@@sidebar-bulk-edit-metadata:Bulk edit metadata`,
      width: '40rem',
      closeOnEscape: true,
      dismissableMask: true,
      closable: true,
      focusOnShow: false,
      styleClass: 'bulk-metadata-dialog',
      data: {
        documents: selectedDocuments,
      },
    })

    ref?.onClose.subscribe((templateDocument?: ArchiveDocumentForm) => {
      // if we were returned an ArchiveDocument (containing the fields to be updated), we assume that the update took
      // place and we need to reload the drafts table
      if (templateDocument) {
        this.resetSelection()
        this.tableReloadSubject.next()

        // if the current document was modified, refresh the metadata view in the right sidebar
        const documentUuids = map(selectedDocuments, document => {
          return document.uuid
        })
        if (documentUuids.includes(this.activeUuid)) {
          this.signalService.sendSignal(Signal.SIGNAL_REFRESH_METADATA)
        }
      }
    })

    // returning the ref is only useful for tests
    return ref
  }

  /**
   * Present the confirmation modal before deleting many documents
   */
  showDeleteMultipleConfirmation () {
    if (this.selectedDocuments.length === 0) return

    const numDocuments = this.selectedDocuments.length
    this.confirmationService.confirm({
      message: $localize`:@@metadata-delete-documents:Are you sure that you want to delete ${numDocuments} documents ?`,
      accept: () => {
        const documentUuids = map(this.selectedDocuments, document => {
          return document.uuid
        })
        this.archiveDocumentsApi.deleteMany(documentUuids).subscribe(() => {
          // reset the currently selected draft documents
          this.resetSelection()
          // reload the drafts table
          this.tableReloadSubject.next()

          // if the current document was deleted, redirect to the empty viewer view
          if (documentUuids.includes(this.activeUuid)) {
            this.router.navigate(['/viewer'])
          }
        })
      },
    })
  }

  /**
   * Delete a specific document
   */
  onDeleteSingleDocument (document: ArchiveDocumentListItem) {
    this.archiveDocumentsApi.delete(document).subscribe(() => {
      // reload the drafts list
      this.tableReloadSubject.next()

      // if we were viewing the deleted document then reset the route coz this document doesn't exist anymore
      if (this.activeUuid === document.uuid) {
        this.router.navigate(['viewer'])
      }
    })
  }

  /**
   *  The user has clicked on a document in the drafts list: update the url which will trigger the pdftron load
   */
  onOpenDocument (document: ArchiveDocumentListItem) {
    this.resetSelection()
    this.router.navigate(['viewer', document.uuid])
  }

  onSelect () {
    this.fileUploadControl.upload()
  }

  /**
   * Custom upload handler for the form.
   * If only 1 file is given, upload it directly, otherwise gathers all files to be uploaded and launches them
   *   with a 'process pool' max number of simultaneous uploads.
   */
  doUpload (event: FileUploadHandlerEvent) {
    if (!event.files.length) {
      return
    }

    if (event.files.length === 1) {
      if (event.files[0].size > (20 * 1024 * 1024)) {
        this.confirmationService.confirm({
          message: $localize`:@@sidebar-document-too-large:The document you are importing is bigger than 20MB.<br /><br />
          Be sure to read scanning recommendation and document size.<br /><br />
          Do you wish to proceed anyway ?`,
          accept: () => {
            this.doSingleUpload(event.files[0])
          },
        })
      } else {
        this.doSingleUpload(event.files[0])
      }
    } else {
      this.showMultipleUploadsModal(event.files)
    }
    // clear the local sidebar files control
    this.fileUploadControl?.clear()
  }

  /**
   * Perform the actual upload for a single document
   */
  private doSingleUpload (file: File) {
    this.archiveDocumentsApi.uploadFile(file).subscribe(res => {
      this.tableReloadSubject.next()
      this.router.navigate(['viewer', res.uuid])
    })
  }

  /**
   * Show the 'multiple uploads modal', which will handle potentially long and error-prone uploads
   */
  showMultipleUploadsModal (files: File[]) {
    const ref = this.dialogService.open(FileUploadModalComponent, {
      header: $localize`:@@sidebar-add-documents:Add new document(s)`,
      width: '40rem',
      closeOnEscape: false,
      dismissableMask: false,
      focusOnShow: false,
      data: {
        files: files,
      },
    })

    ref?.onClose.subscribe(() => {
      // reload drafts list
      this.tableReloadSubject.next()
    })
  }

  /**
   * The drafts list has changed, either on initial load or after modifying the list of drafts
   */
  draftsDataChanged (result: PaginatedArchiveDocumentListItemList) {
    // potentially prevent the user from adding more drafts if they have too many already (https://tasks.acted.org/browse/SI-2286)
    this.disableAddDrafts = result.count > 20
    // reset the current selection to zero rows
    this.resetSelection()
  }

  /**
   * Reset the current selection to zero rows
   */
  resetSelection () {
    this.signalService.sendSignal(Signal.SIGNAL_HIDE_MASK_VIEWER_PANE)
    this.selectedDocuments = []
    this.genericListComponent.selectedItems = []
  }
}
